/**
 * Zod schemas for JSON-encoded fields stored in the database.
 *
 * InstantDB cannot enforce the internal structure of JSON/string fields at the
 * DB layer, so these schemas serve as the runtime contract at read boundaries.
 * Use safeParse() when reading these fields from DB query results so that
 * malformed data produces a clear error rather than a silent wrong-type usage.
 */
import { z } from 'zod';

/**
 * Schema for $users.allowedEventIds.
 * Valid values: "all" (access all events), "" or null (no event access via
 * this field), or a JSON-encoded string array of specific event IDs.
 */
export const AllowedEventIdsSchema = z.union([
    z.literal('all'),
    z.literal(''),
    z.null(),
    z.undefined(),
    z.string().refine(
        s => {
            try {
                const v: unknown = JSON.parse(s);
                return Array.isArray(v) && v.every(x => typeof x === 'string');
            } catch {
                return false;
            }
        },
        { message: 'allowedEventIds must be "all", empty, null, or a JSON string array' }
    ),
]);

export type AllowedEventIds = z.infer<typeof AllowedEventIdsSchema>;

/**
 * Schema for a single node's quantity in the LP solution.
 */
const NodeQuantitySchema = z.object({
    nodeIndex: z.number().int().nonnegative(),
    quantity: z.number().nonnegative(),
});

/**
 * Schema for a single edge flow in the LP solution.
 */
const EdgeFlowSchema = z.object({
    fromNodeIndex: z.number().int().nonnegative(),
    toNodeIndex: z.number().int().nonnegative(),
    value: z.number().nonnegative(),
});

/**
 * Schema for one item type's LP solution (A = node quantities, T = edge flows).
 */
export const ItemTypeSolutionSchema = z.object({
    A: z.array(NodeQuantitySchema),
    T: z.array(EdgeFlowSchema),
});

/**
 * Schema for trips.lpSolution — a record mapping itemTypeId to its solution.
 */
export const LpSolutionSchema = z.record(z.string(), ItemTypeSolutionSchema);

export type LpSolution = z.infer<typeof LpSolutionSchema>;
export type ItemTypeSolution = z.infer<typeof ItemTypeSolutionSchema>;

/**
 * Schema for signupCodes.permissions — keys must match $users boolean column names.
 * passthrough() allows unknown keys to be forwarded without error, but only
 * known permission flags are typed.
 */
export const SignupPermissionsSchema = z
    .object({
        admin: z.boolean().optional(),
        editMapStationsNotes: z.boolean().optional(),
        createEditEventInfo: z.boolean().optional(),
        viewWasteData: z.boolean().optional(),
        editWasteData: z.boolean().optional(),
        generateReports: z.boolean().optional(),
        editTodos: z.boolean().optional(),
        completeTodos: z.boolean().optional(),
        viewInventory: z.boolean().optional(),
        manageInventory: z.boolean().optional(),
        editUsers: z.boolean().optional(),
    })
    .passthrough();

export type SignupPermissions = z.infer<typeof SignupPermissionsSchema>;
